import type { zoomError } from '@lib/zoom/types';
import {
  ZOOM_VALIDATION_ERROR,
  ZOOM_REGISTRATION_ERROR_CODE,
  EMPTY_LINK_ERROR_CODE,
  INVALID_LINK_ERROR_CODE,
  REGISTRATION_LINK_ERROR_CODE,
  EVENT_LINK_ERROR_CODE
} from '@lib/zoom/constants';

type ZoomErrorCodes = Record<number | string, zoomError>;

export const ZOOM_ERROR_CODES: ZoomErrorCodes = {
  200: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'Not support start meeting via tokens',
    errorCode: 200,
    message: 'This meeting/webinar requires StreamAlive to be authorized by Zoom to connect. Additionally, the user that authorized it needs to be added to the Zoom meeting as alternative host.',
    helpUrl: 'https://help.streamalive.com/en/articles/11469104-authorizing-streamalive-access-to-your-zoom'
  },
  3706: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'The meeting number is incorrect.',
    errorCode: 3706,
    message: 'Please double check your meeting link. Zoom reported that the meeting number you provided is incorrect.',
    helpUrl: 'https://help.streamalive.com/en/articles/10473711-zoom-where-to-find-your-zoom-meeting-or-webinar-panelist'
  },
  3707: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'The meeting number is not found.',
    errorCode: 3707,
    message: 'Please double check your meeting link. Zoom reported that it couldn\'t find a meeting with the number you provided.',
    helpUrl: 'https://help.streamalive.com/en/articles/10473711-zoom-where-to-find-your-zoom-meeting-or-webinar-panelist'
  },
  3009: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'Be removed.',
    errorCode: 3009,
    message: 'It looks like the meeting host removed the StreamAlive bot. If this was unintentional please re-connect StreamAlive to your meeting.',
    helpUrl: 'https://help.streamalive.com/en/articles/11332308-streamalive-zoom-connection-troubleshooting-guide#h_2bf3c65265'
  },
  3051: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'Require login',
    errorCode: 3051,
    message: 'StreamAlive couldn\'t join your meeting because it hasn\'t been authorized with your Zoom account. Follow these instructions to set up the authorization.',
    helpUrl: 'https://help.streamalive.com/en/articles/11469104-authorizing-streamalive-access-to-your-zoom'
  },
  3099: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'This webinar requires registration.',
    errorCode: 3099,
    message: 'This Zoom webinar requires a user to register before allowing access. Please register for the webinar and then paste in the webinar join link once you have registered.',
    helpUrl: 'https://help.streamalive.com/en/articles/11332308-streamalive-zoom-connection-troubleshooting-guide#h_92f7a955f9'
  },
  [ZOOM_REGISTRATION_ERROR_CODE]: {
    type: 'ERR_BAD_REQUEST',
    reason: 'Zoom Registration Error',
    errorCode: ZOOM_REGISTRATION_ERROR_CODE,
    message: 'This Zoom meeting requires a user to register before allowing access. Please register for the meeting and then paste in the meeting join link once you have registered.',
    helpUrl: 'https://help.streamalive.com/en/articles/11332308-streamalive-zoom-connection-troubleshooting-guide#h_92f7a955f9'
  },
  [EMPTY_LINK_ERROR_CODE]: {
    type: ZOOM_VALIDATION_ERROR,
    reason: 'Empty Meeting Link Entered',
    errorCode: EMPTY_LINK_ERROR_CODE,
    message: 'You haven\'t entered a Zoom meeting link. Please follow these instructions to learn how to find your Zoom meeting link.',
    helpUrl: 'https://help.streamalive.com/en/articles/9333762-zoom-how-to-use-streamalive-with-zoom-meetings'
  },
  [INVALID_LINK_ERROR_CODE]: {
    type: ZOOM_VALIDATION_ERROR,
    reason: 'Invalid Link Entered',
    errorCode: INVALID_LINK_ERROR_CODE,
    message: 'The meeting link you entered has been rejected by Zoom. Please follow these instructions to learn how to find your Zoom meeting link.',
    helpUrl: 'https://help.streamalive.com/en/articles/9333762-zoom-how-to-use-streamalive-with-zoom-meetings'
  },
  [REGISTRATION_LINK_ERROR_CODE]: {
    type: ZOOM_VALIDATION_ERROR,
    reason: 'Registration Link Entered',
    errorCode: REGISTRATION_LINK_ERROR_CODE,
    message: 'The Zoom link you entered is a registration link, not the meeting link. Follow these instructions to find the correct Zoom meeting link.',
    helpUrl: 'https://help.streamalive.acom/en/articles/11332308-streamalive-zoom-connection-troubleshooting-guide#h_ff7008544e'
  },
  [EVENT_LINK_ERROR_CODE]: {
    type: ZOOM_VALIDATION_ERROR,
    reason: 'Event Link Entered',
    errorCode: EVENT_LINK_ERROR_CODE,
    message: 'The Zoom link you entered is a Zoom events link, not a Zoom meeting link. Follow these instructions to find the correct Zoom events link.',
    helpUrl: 'https://help.streamalive.com/en/articles/11332308-streamalive-zoom-connection-troubleshooting-guide#h_afb22da341'
  }
};
